import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Smoothly tweens from the previous value to the new value over duration ms.
 * Mimics the old GSAP TweenLite.to with roundProps behavior.
 */
export function useJackpotTween(value: number, duration = 1000) {
	const [displayValue, setDisplayValue] = useState(value);
	const prevValueRef = useRef(value);
	const rafRef = useRef<number | null>(null);

	const animate = useCallback(
		(from: number, to: number) => {
			const startTime = performance.now();

			const step = (now: number) => {
				const elapsed = now - startTime;
				const progress = Math.min(elapsed / duration, 1);
				const eased = 1 - Math.pow(1 - progress, 3);
				const current = Math.round(from + (to - from) * eased);
				setDisplayValue(current);
				if (progress < 1) {
					rafRef.current = requestAnimationFrame(step);
				}
			};

			rafRef.current = requestAnimationFrame(step);
		},
		[duration],
	);

	useEffect(() => {
		if (value !== prevValueRef.current) {
			const from = prevValueRef.current;
			prevValueRef.current = value;
			animate(from, value);
		}
		return () => {
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
			}
		};
	}, [value, animate]);

	return displayValue;
}
